import { useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { getMonth, getYear } from '../helpers/dateHelpers';
import { SearchStateInterface } from '../models/SearchInterface';
import { categoriesListData } from '../models/categoryListModel';
import { useContext } from 'react';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';


export const GeneralPairingContext = createContext({
  loading: true,
  list: [] as categoriesListData[],
  getList: (search: SearchStateInterface) => { },
  addToCategory: (search: SearchStateInterface, categoryName: string, pairingId?: string) => { },
  removePairing: (categoryName: string, categoryId: string, search: SearchStateInterface, pairingId?: string) => { },
  hideChangedPairing: (search: SearchStateInterface) => { }
});

export const GeneralPairingProvider = (props) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<categoriesListData[]>([]);
  const { setErrorCode } = useApiError();

  const getList = async (search: SearchStateInterface) => {
    if (!user || !search) {
      return;
    }
    try {
      setLoading(true);
      const requestObject = {
        body: {
          year: getYear(search.selectedDate),
          month: getMonth(search.selectedDate),
        }
      };
      const pairingList = await API.post('crewlife', '/category/list', requestObject);
      setList([...pairingList.categories]);
      setLoading(false);
    } catch (e: any) {
      if (e.response?.status) { setErrorCode(e.response?.status); }
      else { setErrorCode(error.DEFAULT); }
    }
    finally {
      setLoading(false);
    }
  };

  const addToCategory = async (search: SearchStateInterface, categoryName: string, pairingId?: string) => {
    if (!user || !categoryName) {
      return;
    }
    try {
      setLoading(true);
      const requestObject = {
        body: {
          categories: [{
            year: getYear(search.selectedDate),
            month: getMonth(search.selectedDate),
            name: categoryName
          }]
        }
      } as any;

      if (pairingId) {
        requestObject.body.categories[0].pairing = [{
          pairingId
        }];
      }
      await API.post('crewlife', '/category/create', requestObject);
      setLoading(false);
      getList(search);
    } catch (e: any) {
      if (e.response?.status) { setErrorCode(e.response?.status); }
      else { setErrorCode(error.DEFAULT); }
    }
    finally {
      setLoading(false);
    };
  };

  const removePairing = async (categoryName: string, categoryId: string, search: SearchStateInterface, pairingId?: string ) => {
    if (!categoryName || !categoryId) {
      return;
    }

    setLoading(true);
    let requestObject: any = {
      body: {
        categories: [{
          id: categoryId,
          name: categoryName,
          blockyear: getYear(search.selectedDate),
          blockmonth: getMonth(search.selectedDate),
        }]
      }
    };

    if (pairingId) {
      requestObject.body.categories[0].pairing = [{ pairingId }];
    }
    try {
      await API.post('crewlife', '/category/delete', requestObject);
    } catch (e: any) {
      if (e.response?.status) { setErrorCode(e.response?.status); }
      else { setErrorCode(error.DEFAULT); }
    } finally {
      setLoading(false);
    }
  };


  const hideChangedPairing = async (search: SearchStateInterface) => {
    if (!search) {
      return;
    }
    setLoading(true);
    const requestObject = {
      body: {
        blockyear: getYear(search.selectedDate),
        blockmonth: getMonth(search.selectedDate),
      }
    };

    try {
      await API.post('crewlife', '/pairingcategory/update', requestObject);
    } catch (e: any) {
      if (e.response?.status) { setErrorCode(e.response?.status); }
      else { setErrorCode(error.DEFAULT); }
    } finally {
      setLoading(false);
    }
  };


  return (
    <GeneralPairingContext.Provider
      value={{
        loading,
        list,
        getList,
        addToCategory,
        removePairing,
        hideChangedPairing
      }}>
      {props.children}
    </GeneralPairingContext.Provider>
  );
};

export const useGeneralPairing = () => {
  return useContext(GeneralPairingContext);
}